import {
  type DatabaseView,
  DEFAULT_DATABASE_VIEW_SETTINGS
} from '@databases/types/database/view';
import { pick, isEqual, debounce, pickBy } from 'lodash-es';
import { proxy, snapshot, subscribe } from 'valtio';
import { devtools } from 'valtio/utils';

export const viewState = proxy<{ view: DatabaseView }>({
  view: {
    id: '',
    name: '',
    settings: DEFAULT_DATABASE_VIEW_SETTINGS
  }
});

export const setView = (view: DatabaseView) => {
  viewState.view = view;
};

let cachedView: DatabaseView | undefined;
let unsubscribe: (() => void) | undefined;

/**
 * Compares objects removing properties which have value 'undefined'.
 * This is a duplicate function to avoid circular dependency with @databases/utils.
 */
function isEqualOmitUndefined<T>(value: T, other: T): boolean {
  return isEqual(pickBy(value as any, undefined), pickBy(other as any, undefined));
}

/**
 * Returns a cached snapshot of the current database view settings.
 *
 * **Why this exists:**
 * `snapshot()` creates a new object reference every time it's called, even when data hasn't changed.
 * This breaks Redux selector memoization (re-reselect), causing unnecessary recalculations.
 * This function caches the snapshot and updates it via subscription, maintaining referential equality.
 *
 * **Implementation note:**
 * Uses lazy initialization to avoid test failures when valtio is mocked at module load time.
 */
export const getCachedViewSnapshot = (): DatabaseView => {
  if (!unsubscribe && subscribe) {
    unsubscribe = subscribe(viewState.view, () => {
      cachedView = snapshot(viewState.view) as DatabaseView;
    });
  }

  const currentView = snapshot(viewState.view) as DatabaseView;

  if (!cachedView) {
    cachedView = currentView;
    return cachedView;
  }

  if (
    cachedView.id !== currentView.id ||
    !isEqualOmitUndefined(cachedView.settings, currentView.settings)
  ) {
    cachedView = currentView;
  }

  return cachedView;
};

if (process.env.NODE_ENV === 'development') {
  devtools(viewState, 'Valtio View State');
}

let selectionsCachedView: DatabaseView | undefined;
let selectionsCacheUnsubscribe: (() => void) | undefined;

/**
 * In the selections API, when we build the adapter or actions for the action bar / context menu,
 * we need the most up to date state that busts whenever anything on the entire viewState changes.
 */
export const getCachedViewSnapshotForSelections = (): DatabaseView => {
  if (!selectionsCacheUnsubscribe && subscribe) {
    selectionsCacheUnsubscribe = subscribe(viewState, () => {
      selectionsCachedView = snapshot(viewState.view) as DatabaseView;
    });
  }

  if (!selectionsCachedView) {
    selectionsCachedView = snapshot(viewState.view) as DatabaseView;
  }

  return selectionsCachedView;
};

/**
 * Debounced subscription for view changes that invalidate cell selection
 * (view id, sorts, filters, fields, grouping, **layout**, etc.). Used by
 * `createViewChangesInput` to dispatch `deselectAll` without rebuilding the
 * whole selections machine. For re-running feature `isEnabled` on layout,
 * bootstrap uses `subscribeViewIdOrLayoutTypeForSelections` instead.
 */
export function createViewStateSubscription(onChange: VoidFunction) {
  const debouncedOnChange = debounce(onChange, 50);
  const SETTINGS_KEYS_TO_WATCH = [
    'sort',
    'sorts',
    'filter',
    'fields',
    'calculations',
    'grouping',
    'layout'
  ] as const;

  const getTrackedState = () => {
    const snap = snapshot(viewState.view);

    return {
      id: snap.id,
      settings: pick(snap.settings, SETTINGS_KEYS_TO_WATCH)
    };
  };

  let prevState = getTrackedState();

  const unsubscribe = subscribe(viewState, () => {
    const currentState = getTrackedState();

    // Fire if the ID changed OR if the specific settings changed
    if (
      prevState.id !== currentState.id ||
      !isEqual(prevState.settings, currentState.settings)
    ) {
      prevState = currentState;

      debouncedOnChange();
    }
  });

  return () => {
    unsubscribe();
    debouncedOnChange.cancel();
  };
}

/**
 * Notifies when the active database view’s id or `settings.layout.type`
 * changes. Used by the selections machine bootstrap to rebuild inputs/effects
 * so feature `isEnabled` gates see the new layout (e.g. table vs gallery).
 *
 * Narrower than `createViewStateSubscription` (sorts, filters, fields, …),
 * which is meant for selection invalidation without a full machine rebuild.
 */
export function subscribeViewIdOrLayoutTypeForSelections(
  onChange: VoidFunction
): VoidFunction {
  const readIdentity = () => {
    const v = snapshot(viewState.view) as DatabaseView;

    return { id: v.id, layoutType: v.settings.layout.type };
  };

  let prev = readIdentity();

  return subscribe(viewState, () => {
    const next = readIdentity();

    if (prev.id !== next.id || prev.layoutType !== next.layoutType) {
      prev = next;
      onChange();
    }
  });
}
